import { Checkbox } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { SearchConversationHit, Tag } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { MouseEvent, useCallback } from 'react';
import { Writable } from 'ts-essentials';

import { getMetaConversationName } from 'app/utils/conversation';
import { ConversationDetails } from 'components/conversation/conversationDetails';
import { useIsRestrictedFromQueue } from 'utils/user/useUserQueues';

import searchResultStyles from '../../../../../searchResults.module.scss';
import { SearchResult } from '../../../../result/searchResult';

import styles from './conversationResult.module.scss';
import { convertHitToMetaConversation } from './utils/convertHitToMetaConversation';

type ConversationResultProps = {
  hit: SearchConversationHit;
  isSelectable: boolean;
  isSelected: boolean;
  onRowSelect: (hit: SearchConversationHit) => void;
  onRowClick: (hit: SearchConversationHit) => void;
} & PropsWithClassName;

/**
 * Represents a component that is used to render the conversation search result
 * item
 */
export const ConversationResult = ({
  className,
  hit,
  isSelectable,
  isSelected,
  onRowSelect,
  onRowClick,
}: ConversationResultProps): JSX.Element => {
  const isEventTargetExcluded = useCallback(
    ({ target }: MouseEvent<HTMLDivElement>) => {
      const targetEl = target as Element;
      return (
        targetEl.tagName === 'LABEL' || targetEl.matches('[type="checkbox"]')
      );
    },
    [],
  );
  const metaConversation = convertHitToMetaConversation(hit);
  const isRestricted = useIsRestrictedFromQueue(
    metaConversation.conversation.queue || null,
  );
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (
        isRestricted === undefined ||
        isRestricted ||
        isEventTargetExcluded(event)
      ) {
        return;
      }
      onRowClick(hit);
    },
    [hit, isEventTargetExcluded, onRowClick, isRestricted],
  );

  const title = getMetaConversationName(metaConversation);

  return (
    <SearchResult
      className={classNames(className, {
        [searchResultStyles.rowClickable]: isRestricted === false,
      })}
      onClick={handleClick}
      content={
        <div className={styles.conversationSummaryWrapper}>
          {isSelectable && (
            <Checkbox
              onChange={() => onRowSelect(hit)}
              checked={isSelected}
              id={hit.id}
              name={hit.id}
            />
          )}

          <ConversationDetails
            metaConversation={metaConversation}
            conversationName={title}
            showMessageCount
            showTags
            tags={hit.tags as Writable<Tag>[]}
            conversationUrl={`/conversation/${hit.numeric_id}`}
            linkToConversation={false}
          />
        </div>
      }
    />
  );
};
